import React, { useState } from "react";
import { Form as FinalForm, Field as FinalField, FormSpy } from "react-final-form";
import { Button, Form as SemanticForm, Message, Segment, Icon } from "semantic-ui-react";
import { privacyPolicyLink, termsOfServiceLink } from "../../config/config";
import { TextInput, SelectInput } from "../../utils/components";
import {
  composeValidators,
  required,
  validateUsedUser,
  validateEmailString,
  validateUsedEmail,
  validatePassword
} from "../../utils/formValidation";
import { exists } from "../../utils/utils";

const RegisterAccountForm = ({
  onSubmit,
  onCancel,
  userTypes,
  initialValues = {},
  publicRoles = [],
  showSuccessMessage,
  showFailureMessage,
  loading,
  successMessageHeader,
  successMessageContent,
  failureMessageHeader,
  failureMessageContent
}) => {
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const DisableOnSuccessField = props => (
    <FinalField {...props} disabled={loading || showSuccessMessage} />
  );
  const verifyWithBackend = async values => {
    const errors = {};
    let emailError;
    if (initialValues.email !== values.email) {
      emailError = await validateUsedEmail(values.email);
    }
    let userError;
    if (initialValues.user !== values.user) {
      userError = await validateUsedUser(values.user);
    }
    if (exists(emailError)) {
      errors.email = emailError;
    }
    if (exists(userError)) {
      errors.user = userError;
    }
    return errors;
  };

  const userMutator = ([emailValue], state) => {
    if (
      !state.fields["user"].active &&
      (!state.fields["user"].touched || state.fields["user"].lastFieldState.value === undefined)
    ) {
      state.fields["user"].change(emailValue);
    }
  };

  return (
    <FinalForm
      onSubmit={onSubmit}
      mutators={{ userMutator }}
      render={({ handleSubmit, invalid }) => (
        <SemanticForm onSubmit={handleSubmit} size="large" style={{ textAlign: "left" }}>
          <Segment>
            {publicRoles && publicRoles.length > 0 && (
              <SemanticForm.Group widths="equal">
                <DisableOnSuccessField
                  component={SelectInput}
                  aria-label="Region selector"
                  label="Region:"
                  defaultValue={publicRoles[0].id}
                  name="roleId"
                  required
                  placeholder="Select region"
                  validate={required(true)}
                  options={publicRoles.map(publicRole => ({
                    key: publicRole.id,
                    text: publicRole.region.name,
                    value: publicRole.id
                  }))}
                />
              </SemanticForm.Group>
            )}

            <SemanticForm.Group widths="equal">
              <DisableOnSuccessField
                component={TextInput}
                fluid
                label="Name:"
                placeholder="Please enter your full name"
                initialValue={initialValues.name}
                name="name"
                icon="user"
                iconPosition="left"
                validate={required(true)}
                required
              />
            </SemanticForm.Group>
            <SemanticForm.Group widths="equal">
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="Email:"
                initialValue={initialValues.email}
                name="email"
                icon="mail"
                iconPosition="left"
                placeholder="example@email.com"
                required
                validate={composeValidators(required(true), validateEmailString)}
              />
              <FormSpy
                subscription={{ email: true }}
                render={({ form }) => {
                  const emailField = form?.getFieldState("email");
                  const isEmailValid = emailField?.valid;
                  if (isEmailValid) {
                    const userField = form?.getFieldState("user");
                    // Note (@nicoluce): Mutator is wrapped inside setTimeout because of
                    // https://github.com/final-form/react-final-form/issues/809
                    setTimeout(() => form.mutators.userMutator(emailField.value, userField.value));
                  }
                  return <></>;
                }}
              />
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="Username:"
                name="user"
                initialValue={initialValues.user}
                icon="address card"
                iconPosition="left"
                placeholder="Username"
                validate={required(true)}
                required
              />
            </SemanticForm.Group>
            <SemanticForm.Group widths="equal">
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="Password:"
                name="password"
                placeholder="Password"
                validate={required(true)}
                type={isPasswordVisible ? "text" : "password"}
                required
                iconPosition="left"
                icon={
                  <Icon
                    link
                    name={isPasswordVisible ? "eye" : "eye slash"}
                    onClick={() => setPasswordVisible(!isPasswordVisible)}
                  />
                }
              />
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="Confirm your password:"
                name="confirm"
                placeholder="Confirm password"
                validate={required(true)}
                iconPosition="left"
                icon={
                  <Icon
                    link
                    name={isPasswordVisible ? "eye" : "eye slash"}
                    onClick={() => setPasswordVisible(!isPasswordVisible)}
                  />
                }
                type={isPasswordVisible ? "text" : "password"}
              />
            </SemanticForm.Group>
            <DisableOnSuccessField
              fluid
              component={TextInput}
              defaultValue={initialValues.jobTitle}
              label="Job title:"
              name="jobTitle"
              placeholder="Job title"
              validate={required(true)}
              required
            />
            <DisableOnSuccessField
              fluid
              component={TextInput}
              defaultValue={initialValues.jobTitle}
              label="Phone:"
              name="phone"
              placeholder="Phone"
            />
            <DisableOnSuccessField
              component={TextInput}
              label="Organization:"
              name="organization"
              placeholder="Organization"
              required
              validate={required(true)}
            />
            <DisableOnSuccessField
              aria-label="User type selection"
              component={SelectInput}
              label="User type:"
              defaultValue={initialValues.userType}
              name="userTypeId"
              required
              placeholder="Select type"
              validate={required(true)}
              options={userTypes.map(userType => ({
                key: userType.id,
                text: userType.name,
                value: userType.id
              }))}
            />
            <SemanticForm.Group widths="equal">
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="Street:"
                name="street"
                placeholder="Street"
              />
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="City:"
                name="city"
                placeholder="City"
              />
            </SemanticForm.Group>
            <SemanticForm.Group widths="equal">
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="State / Province:"
                name="state"
                placeholder="State / Province"
              />
              <DisableOnSuccessField
                fluid
                component={TextInput}
                label="Zip / Postal Code:"
                name="zip"
                placeholder="Zip / Postal Code"
              />
            </SemanticForm.Group>
            <DisableOnSuccessField
              component={TextInput}
              label="Country:"
              name="contry"
              placeholder="Country"
            />
            {showSuccessMessage && (
              <Message positive>
                <Message.Content>
                  <Message.Header>{successMessageHeader}</Message.Header>
                  {successMessageContent}
                </Message.Content>
              </Message>
            )}
            {showFailureMessage && (
              <Message negative>
                <Message.Content>
                  <Message.Header>{failureMessageHeader}</Message.Header>
                  {failureMessageContent}
                </Message.Content>
              </Message>
            )}
            {!showSuccessMessage && !showFailureMessage && (
              <Message info>
                <p>
                  By confirming this account you agree to our{" "}
                  <a href={termsOfServiceLink} download>
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href={privacyPolicyLink} download>
                    Privacy Policy
                  </a>
                  .
                </p>
              </Message>
            )}
            <Button.Group widths="2">
              <Button
                type="button"
                content="Cancel"
                disabled={loading || showSuccessMessage}
                onClick={() => onCancel()}
              />
              <Button.Or />
              <Button primary type="submit" disabled={invalid || loading || showSuccessMessage}>
                Confirm Account
              </Button>
            </Button.Group>
          </Segment>
        </SemanticForm>
      )}
      validate={values => {
        const errors = {};
        if (!values.password) {
          return;
        }
        errors.password = validatePassword(values.password);
        if (values.confirm && values.password !== values.confirm) {
          errors.confirm = "Passwords do not match";
        }
        return Object.keys(errors).length !== 0 || loading || showSuccessMessage
          ? errors
          : verifyWithBackend(values);
      }}
    />
  );
};

export default RegisterAccountForm;
